import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Mail, ArrowRight, Lock, Eye, EyeOff } from 'lucide-react';
import { theme } from '../styles/theme';
import LogoComponent from '../components/Logo';
import { useAuth } from '../context/AuthContext';

const LoginContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.backgroundSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]};
`;

const LoginCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.lg};
  padding: ${theme.spacing[10]} ${theme.spacing[8]};
  width: 100%;
  max-width: 560px;
  text-align: center;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${theme.spacing[8]};
  width: 135px;
  height: auto;
  margin-left: auto;
  margin-right: auto;
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[2]};
  font-family: ${theme.typography.fontFamily.display};
`;

const Subtitle = styled.p`
  color: ${theme.colors.textMuted};
  margin-bottom: ${theme.spacing[8]};
  font-size: ${theme.typography.fontSize.base};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  text-align: left;
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textPrimary};
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${theme.spacing[3]};
  color: ${theme.colors.textMuted};
  z-index: 1;
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[10]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  transition: all ${theme.transitions.base};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }
  
  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const MagicLinkButton = styled.button`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.primary[500]};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  
  &:hover:not(:disabled) {
    background: ${theme.colors.primary[600]};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;


const HelperText = styled.p`
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  margin-top: ${theme.spacing[2]};
  text-align: left;
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success[100]};
  color: ${theme.colors.success[700]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  text-align: center;
  margin-top: ${theme.spacing[4]};
`;

const SignUpRow = styled.p`
  margin: ${theme.spacing[6]} 0 0 0;
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};

  a {
    color: ${theme.colors.primary[500]};
    text-decoration: none;
    font-weight: ${theme.typography.fontWeight.medium};

    &:hover {
      text-decoration: underline;
    }
  }
`;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, login, requestMagicLink, verifyMagicLink } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginMode, setLoginMode] = useState<'password' | 'magic'>('password');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check for magic link token in URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      handleMagicLinkVerification(token);
    }
  }, [searchParams]);

  // Redirect if already authenticated
  useEffect(() => {
    if (state.isAuthenticated) {
      navigate('/');
    }
  }, [state.isAuthenticated, navigate]);

  const handleMagicLinkVerification = async (token: string) => {
    setIsLoading(true);
    const success = await verifyMagicLink(token);
    setIsLoading(false);
    
    if (success) {
      navigate('/');
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    
    setIsLoading(true);
    const success = await login(email, password);
    setIsLoading(false);
    
    if (success) {
      navigate('/');
    }
  };

  const handleMagicLinkRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    const success = await requestMagicLink(email);
    setIsLoading(false);
    
    if (success) {
      setShowSuccess(true);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LogoContainer>
          <LogoComponent size={50} />
        </LogoContainer>
        
        <Title>Sign in</Title>
        <Subtitle>
          Please sign-in below using your email and password or via a magic link.
        </Subtitle>

        {/* Login Mode Toggle */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={() => setLoginMode('password')}
            style={{
              padding: '8px 16px',
              background: loginMode === 'password' ? theme.colors.primary[500] : 'transparent',
              color: loginMode === 'password' ? 'white' : theme.colors.textPrimary,
              border: `1px solid ${theme.colors.primary[500]}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Password
          </button>
          <button
            type="button"
            onClick={() => setLoginMode('magic')}
            style={{
              padding: '8px 16px',
              background: loginMode === 'magic' ? theme.colors.primary[500] : 'transparent',
              color: loginMode === 'magic' ? 'white' : theme.colors.textPrimary,
              border: `1px solid ${theme.colors.primary[500]}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Magic Link
          </button>
        </div>

        {loginMode === 'password' ? (
          <Form onSubmit={handlePasswordLogin}>
            <InputGroup>
              <Label htmlFor="email">Email address</Label>
              <InputContainer>
                <InputIcon>
                  <Mail size={16} />
                </InputIcon>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </InputContainer>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="password">Password</Label>
              <InputContainer>
                <InputIcon>
                  <Lock size={16} />
                </InputIcon>
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: theme.colors.textMuted,
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </InputContainer>
              <div style={{ textAlign: 'right', marginTop: '6px' }}>
                <Link
                  to="/forgot-password"
                  style={{
                    fontSize: '13px',
                    color: theme.colors.primary[500],
                    textDecoration: 'none',
                    fontWeight: 500,
                  }}
                >
                  Forgot password?
                </Link>
              </div>
            </InputGroup>

            <MagicLinkButton type="submit" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign in'}
              {!isLoading && <ArrowRight size={16} />}
            </MagicLinkButton>
          </Form>
        ) : (
          <Form onSubmit={handleMagicLinkRequest}>
            <InputGroup>
              <Label htmlFor="email">Email address</Label>
              <InputContainer>
                <InputIcon>
                  <Mail size={16} />
                </InputIcon>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </InputContainer>
              <HelperText>We'll email you a magic link to sign-in.</HelperText>
            </InputGroup>

            <MagicLinkButton type="submit" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Magic Link'}
              {!isLoading && <ArrowRight size={16} />}
            </MagicLinkButton>
          </Form>
        )}

        {/* Display error message */}
        {state.error && (
          <div style={{
            background: theme.colors.error?.[100] || '#fee',
            color: theme.colors.error?.[700] || '#c00',
            padding: '12px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            textAlign: 'center',
            marginTop: '16px',
          }}>
            {state.error}
          </div>
        )}

        {showSuccess && (
          <SuccessMessage>
            Check your email for a magic link to sign in
          </SuccessMessage>
        )}

        <SignUpRow>
          First time here? <Link to="/register">Request access &rarr;</Link>
        </SignUpRow>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginPage;